import './carousel.css';
import YoutubeEmbed from '../../../components/AppVideo/AppVideo';
import { useTranslation } from 'react-i18next';
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import chevronLeft from '../../../assets/images/chevron-left.png'
import chevronRight from '../../../assets/images/chevron-right.png'

const getFullImageLink = (image: string) => {
  const env = process.env["REACT_APP_ENV_LOADED"];
  let url = `https://content-ndc-portal.aircanada.com`; //default to prod
  if (env === "DEV" || env === "INT" || env === "CRT") {
    url = `https://content-ndc-portal-int.aircanada.com`;
  } else if (env === "BAT" || env === "PREPROD") {
    url = `https://content-ndc-portal-bat.aircanada.com`;
  }
  return url + image;
}




const Carousel: React.FC<{ logoHeight: number }> = ({ logoHeight }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [filteredData, setFilteredData] = useState<any[]>([]);

  const sliderRef = useRef<any>(null);

  const isSmallScreen = useMediaQuery('(max-width:480px)');
  const isMediumScreen = useMediaQuery('(max-width:899px)');
  const isLargeScreen = useMediaQuery('(max-width:1024px)');


  // Determine the base margin value based on the screen size
  let baseMargin;
  if (isSmallScreen) {
    baseMargin = 0; // Example value for small screens
  } else if (isMediumScreen) {
    baseMargin = 5; // Example value for medium screens
  } else if (isLargeScreen) {
    baseMargin = 35; // Example value for large screens
  } else {
    baseMargin = 40; // Default value for extra large screens
  }


  const isValidDateWindow = (startDate: string, endDate: string): boolean => {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: 'America/New_York'
    };
    const formatter = new Intl.DateTimeFormat('en-US', options);
    const formattedDateTimeString = formatter.format(now);
    const parsedDate = new Date(formattedDateTimeString);
    const start = new Date(startDate);
    const end = new Date(endDate);

    return parsedDate >= start && parsedDate <= end;
  };

  const filterDataByDate = (data: any[]): any[] => {
    return data.filter(item => {
      if (item.start_date && item.end_date) {
        return isValidDateWindow(item.start_date, item.end_date);
      }
      return true; // Include items without date properties
    });
  };

  useEffect(() => {
    if (filteredData.length === 1) {
      const style = document.createElement('style');
      style.innerHTML = `
        .slick-slide.slick-active.slick-center.slick-current {
          left: 50% !important;
          transform: translateX(-50%) !important;
        }
      `;
      document.head.appendChild(style);

      return () => {
        document.head.removeChild(style);
      };
    }
  }, [filteredData.length]);

  useEffect(() => {
    const data: any = t('home_carousel_home_caroussel', {
      returnObjects: true,
      defaultValue: []
    } as any);

    const updateFilteredData = () => {
      const newFilteredData = filterDataByDate(data);
      if (JSON.stringify(newFilteredData) !== JSON.stringify(filteredData)) {
        setFilteredData(newFilteredData);
      }
    };

    updateFilteredData();
    const intervalId = setInterval(updateFilteredData, 1000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const handleNext = () => {
    sliderRef.current?.slickNext();
    setCurrentIndex((prevIndex) => (prevIndex + 1) % filteredData.length);
  };

  const handlePrev = () => {
    sliderRef.current?.slickPrev();
    setCurrentIndex((prevIndex) => (prevIndex - 1 + filteredData.length) % filteredData.length);
  };

  const handlePageClick = (index: number) => {
    const totalSlides = sliderRef.current?.innerSlider?.props.children.length;

    if (currentIndex !== undefined && totalSlides !== undefined) {
      const distance = index - currentIndex;
      const half = totalSlides / 2;

      if (Math.abs(distance) > half) {
        if (distance > 0) {
          sliderRef.current?.slickPrev();
        } else {
          sliderRef.current?.slickNext();
        }
      } else {
        if (distance > 0) {
          for (let i = 0; i < distance; i++) {
            sliderRef.current?.slickNext();
          }
        } else {
          for (let i = 0; i < Math.abs(distance); i++) {
            sliderRef.current?.slickPrev();
          }
        }
      }
    }
    setCurrentIndex(index);
  };
  const handleNavigation = (link: string) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (link.startsWith('http')) {
      window.open(link, '_blank');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      navigate(`/${i18n.language}${link}`);
    }
  };

  const settings = {
    dots: true,
    infinite: filteredData?.length > 1,
    speed: 500,
    afterChange: (current: number) => {
      setCurrentIndex(current);
    },
    slidesToShow: 1,
    variableWidth: true,
    slidesToScroll: 1,
    centerMode: true,
    arrows: false,
    centerPadding: '0',
    customPaging: (i) => (
      <button
        className={`carousel-dot ${currentIndex === i ? 'active' : ''}`}
        onClick={() => handlePageClick(i)}
        role="tab"
        aria-selected={currentIndex === i}
        aria-label={`Go to slide index: ${i + 1}`}
      >
      </button>
    ),
    appendDots: (dots) => (
      filteredData?.length > 1 ? <div role="tablist" aria-label="Pagination">
        {dots}
      </div> : <></>
    ),
    responsive: [
      {
        breakpoint: 2560,
        settings: {
          slidesToShow: 1,
        },
      },
      {
        breakpoint: 1440,
        settings: {
          slidesToShow: 1,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 1,
        },
      },
      {
        breakpoint: 899,
        settings: {
          slidesToShow: 1,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  return (
    <div style={{ marginTop: logoHeight / 2 + baseMargin + 'px', }}>
      <Slider ref={sliderRef} {...settings} className="custom-slider carousel-container">
        {filteredData.map((item, index) => (
          <div
            key={index}
            style={{ height: 'inherit', width: 'inherit' }}
            onClick={() => handlePageClick(index)}
            role="tabpanel"
            aria-hidden={currentIndex !== index}
            tabIndex={currentIndex === index ? 0 : -1}
          >
            {!(item.video === "TRUE") ? (
              <img
                src={getFullImageLink(isSmallScreen ? item?.media_mobile?.url : item?.media?.url)}
                alt={isSmallScreen ? item?.media_mobile?.alt : item?.media?.alt}
                className="carousel-image large"
              />
            ) : (
              <div className="carousel-video">
                <YoutubeEmbed src={item.uri.uri?.split("=")?.[1]} />
              </div>
            )}
            {item.uri && item?.video !== "TRUE" && (
              <>
                <div className="carousel-info">
                  <h2 className={`carousel-title ${!isSmallScreen ? 'half-width' : ''}`} dangerouslySetInnerHTML={{ __html: item.title }} />
                  <span className={`carousel-description ${!isSmallScreen ? 'half-width' : ''}`} dangerouslySetInnerHTML={{ __html: item.Description }} />
                  <button
                    className="carousel-action-btn"
                    onClick={() => handleNavigation(item.uri.uri)}
                    aria-label={item.button_accessibility}
                  >
                    {item.button}
                  </button>
                </div>
              </>
            )}
            {filteredData.length > 1 && index === currentIndex && (
              <>
                <button
                  className="carousel-btn left"
                  onClick={handlePrev}
                  aria-label="Previous"
                  tabIndex={0}
                >
                  <img src={chevronLeft} alt="chevron-left" style={{ padding: '.5rem 0', width: 'inherit', height: 'inherit' }} />
                </button>
                <button
                  className="carousel-btn right"
                  onClick={handleNext}
                  aria-label="Next"
                  tabIndex={0}
                >
                  <img src={chevronRight} alt="chevron-right" style={{ padding: '.5rem 0', width: 'inherit', height: 'inherit' }} />
                </button>
              </>
            )}
          </div>
        ))}
      </Slider>
    </div>
  );
};

export default Carousel;
